import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  getAccessToken,
  getUser,
  getUserPrimaryRole,
  getUserTenantId,
  login,
} from './auth';
import { normalizeApiUrl } from '../utils/api-base-url';
import { isTenantInactiveError, showTenantInactiveNotice } from './tenantInactive';
import { forbiddenMessageFor } from './apiErrors';

const API_URL = normalizeApiUrl(import.meta.env.VITE_API_URL);

const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use(
  async (config) => {
    // Do not inherit the JSON default for browser FormData. Leaving this
    // unset lets the browser generate the multipart boundary FastAPI needs.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      config.headers.delete?.('Content-Type');
      config.headers.delete?.('content-type');
    }
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
        const user = await getUser();
        if (user?.profile) {
          config.headers['X-User-ID'] = user.profile.sub || '';
          config.headers['X-User-Role'] = getUserPrimaryRole(user);
          // Preserve explicit tenant overrides from caller (e.g., Action Cards tenant selector).
          if (!config.headers['X-Tenant-ID'] && !config.headers['x-tenant-id']) {
            config.headers['X-Tenant-ID'] = getUserTenantId(user);
          }
        }
      } else {
        await login();
        return Promise.reject(new Error('Token unavailable, redirecting to login'));
      }
    } catch (error) {
      console.error('Failed to get IAM access token', error);
      await login();
      return Promise.reject(error);
    }
    return config;
  },
  (error) => { return Promise.reject(error); }
);

const NON_CRITICAL_401_ENDPOINTS = ['/users/track-login', '/admin/notifications/count'];

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const requestUrl = error.config?.url || '';
    const isNonCritical = NON_CRITICAL_401_ENDPOINTS.some(ep => requestUrl.includes(ep));
    if (error.response?.status === 401 && !isNonCritical) {
      login();
    } else if (isTenantInactiveError(error)) {
      showTenantInactiveNotice();
    } else if (error.response?.status === 403) {
      // Pages that print error.message would otherwise show "Request failed
      // with status code 403" and read like a bug. A switched-off feature is
      // not blamed on the role. response.data stays as the server sent it for
      // callers that inspect detail/code.
      error.message = forbiddenMessageFor(error);
    }
    return Promise.reject(error);
  }
);

export { apiClient };
export default apiClient;
